import { HttpService } from 'core/modules/network-service';
import { get, merge } from 'lodash';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems,  } from 'resolvers/getVisibleEntities';
import { resolveEntityCellData } from  'resolvers/resolveEntityCellData';
import { REDUCER } from 'constants/entity-names';
import { getStore } from 'store';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberPrescriptionDetailSummary defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberPrescriptionDetailSummaryService } from 'services/memberPrescriptionDetailSummary';
 *
 * // create service instance and send/cancel request
 * const memberPrescriptionDetailSummaryService = new MemberPrescriptionDetailSummaryService();
 * const result = await memberPrescriptionDetailSummaryService.fetchData( '<user-id>' ); // fetch data
 * memberPrescriptionDetailSummaryService.cancel(); // cancel an active request
 */
export class MemberPrescriptionDetailSummaryService extends HttpService {

    // make request and fetch data
    async fetchComponentData( { url, data } ) {

        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data,
                    {
                        context: {
                            callerType
                        }
                    }
                ),
            } );

            const component = get( response, 'body.component', {} );
            const visibility = get( component, 'visibility', {} );
            const fields = get( component, 'structure.fields', {} );
            const fieldActions = get( component, 'structure.fieldActions', {} );
            const sections = get( component, 'structure.sections', {} );
            const widgetConfig =  get( component, 'structure.widgetConfig', {} );
            const navActions = get( response, 'body.component.structure.navActions', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    rxData: get( response, 'body.component.data.state', {} ),
                    fields: resolveEntityCellData( { items: getVisibleItems( fields, visibility ), component } ),
                    sections: resolveEntityCellData( { items: getVisibleItems( sections, visibility ), component } ),
                    fieldActions,
                    navActions,
                    visibility,
                    widgetConfig
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // make request and fetch data
    async initiateCheckoutProcess( { url, data } ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR )
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // initiates the disable auto refill request
    async disableAutoData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const memberId = data.data.memberId;

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            
            data: merge( data, {
                context: {
                    callId,
                    callType,
                    memberId,
                }
            } ),
        } );

        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', [] ),
                ...get( response, 'body.component.structure', [] )
            }
        };
    }

    // make request and fetch data
    async enableAuto( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } )
            } );

            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                return {
                    _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                    _data: get( response, 'body.component', {} ),
                };
            }

            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                _data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };
            
        } catch( error ) {
            return {
                _status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async fetchToDoWidgetInfo ( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    options: get( response, 'body.component.data.options', {} ),
                    state: get( response, 'body.component.data.state', {} ),
                    ...get( response, 'body.component.structure', {} ),
                }
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // submit next refill date change request
    async submitChangeNextRefillDate( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data' )
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }
}
